export const VALUES = "values";
export const RANGE = "range";

export const emptySelection = { facets: {} };

const normalizeId = (id) => String(id);

const toCanonicalValues = (values = []) =>
  Array.from(new Set(values.map(normalizeId))).sort();

export const valuesSelection = (values) => ({
  kind: VALUES,
  values: toCanonicalValues(values),
});

export const rangeSelection = ({ min, max }) => ({
  kind: RANGE,
  min,
  max,
});

const cloneFacetSelection = (facetSelection) =>
  facetSelection?.kind === RANGE
    ? rangeSelection(facetSelection)
    : valuesSelection(facetSelection?.values);

export const cloneSelection = (selection) => ({
  facets: Object.entries(selection?.facets || {}).reduce(
    (facets, [facetId, facetSelection]) => ({
      ...facets,
      [normalizeId(facetId)]: cloneFacetSelection(facetSelection),
    }),
    {},
  ),
  ...(selection?.sort ? { sort: selection.sort } : undefined),
});

const areValuesEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, i) => value === right[i]);

const areFacetSelectionsEqual = (left, right) => {
  if (left?.kind !== right?.kind) {
    return false;
  }

  return left.kind === RANGE
    ? left.min === right.min && left.max === right.max
    : areValuesEqual(left.values, right.values);
};

/**
 * Selections are stored canonically (ids stringified, values sorted and
 * deduplicated) so equality is a structural walk rather than a serialise.
 */
export const areSelectionsEqual = (left, right) => {
  const leftFacets = left?.facets || {};
  const rightFacets = right?.facets || {};
  const leftIds = Object.keys(leftFacets);

  return (
    left?.sort === right?.sort &&
    leftIds.length === Object.keys(rightFacets).length &&
    leftIds.every((facetId) =>
      areFacetSelectionsEqual(leftFacets[facetId], rightFacets[facetId]),
    )
  );
};

export const isSelectionEmpty = (selection) =>
  Object.keys(selection?.facets || {}).length === 0 && !selection?.sort;

export const getSelectedValues = (selection, facetId) => {
  const facetSelection = selection?.facets?.[normalizeId(facetId)];

  return facetSelection?.kind === VALUES ? facetSelection.values : [];
};

export const getSelectedRange = (selection, facetId) => {
  const facetSelection = selection?.facets?.[normalizeId(facetId)];

  return facetSelection?.kind === RANGE ? facetSelection : undefined;
};

const withFacetSelection = (selection, facetId, facetSelection) => {
  const nextSelection = cloneSelection(selection);
  const normalizedFacetId = normalizeId(facetId);

  if (facetSelection) {
    nextSelection.facets[normalizedFacetId] = facetSelection;
  } else {
    delete nextSelection.facets[normalizedFacetId];
  }

  return nextSelection;
};

export const toggleFacetValueInSelection = (
  selection,
  { facetId, valueId, isSelected },
) => {
  const selectedValues = getSelectedValues(selection, facetId);
  const normalizedValueId = normalizeId(valueId);
  const shouldSelect =
    isSelected === undefined
      ? !selectedValues.includes(normalizedValueId)
      : isSelected;
  const nextValues = shouldSelect
    ? selectedValues.concat(normalizedValueId)
    : selectedValues.filter((value) => value !== normalizedValueId);

  return withFacetSelection(
    selection,
    facetId,
    nextValues.length > 0 ? valuesSelection(nextValues) : undefined,
  );
};

export const clearFacetInSelection = (selection, facetId) =>
  withFacetSelection(selection, facetId, undefined);

export const setRangeInSelection = (selection, facetId, range) =>
  withFacetSelection(
    selection,
    facetId,
    range ? rangeSelection(range) : undefined,
  );

export const setSortInSelection = (selection, sort) => {
  const nextSelection = cloneSelection(selection);

  if (sort) {
    nextSelection.sort = sort;
  } else {
    delete nextSelection.sort;
  }

  return nextSelection;
};

export const clearRefinementsFromSelection = (selection) => ({
  facets: {},
  ...(selection?.sort ? { sort: selection.sort } : undefined),
});
